import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { calculateTeamPoints, calculateMaxPotential } from "../utils/scoring"
import { getActiveYear } from "../utils/activeYear"

interface Team {
  id: number
  name: string
  seed: number
  region: string
  wins: number
  is_eliminated: boolean
}

interface ChampionTeam {
  id: number
  name: string
  seed: number
}

interface Entry {
  id: string
  entry_name: string
  points: number
  is_paid: boolean
  created_at: string
  picks: { team: Team }[]
  rank?: number
  totalEntries?: number
  champion_team?: ChampionTeam | null
  championship_total_points?: number | null
  championship_total_fouls?: number | null
  in_champion_contest?: boolean
  champion_contest_paid?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SupabaseEntry = any

export default function MyEntries() {
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null)
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  const [entryFee, setEntryFee] = useState<number>(20)
  const [entriesOpen, setEntriesOpen] = useState(false)
  const [contestLive, setContestLive] = useState(false)
  const [paymentLink, setPaymentLink] = useState<string>('https://venmo.com/u/bfwright')
  const [championEntryFee, setChampionEntryFee] = useState<number>(5)
  const [championGameOpen, setChampionGameOpen] = useState(false)

  useEffect(() => {
    async function loadEntries() {
      const year = await getActiveYear()
      setActiveYearState(year)

      // Load entry fee, entries_open, contest_live, payment_link, and champion config from years table
      const { data: yearData } = await supabase
        .from('years')
        .select('entry_fee, entries_open, contest_live, payment_link, champion_entry_fee, champion_game_open')
        .eq('year', year)
        .single()
      
      if (yearData?.entry_fee) {
        setEntryFee(yearData.entry_fee)
      }
      if (yearData?.payment_link) {
        setPaymentLink(yearData.payment_link)
      }
      setEntriesOpen(yearData?.entries_open || false)
      setContestLive(yearData?.contest_live || false)
      setChampionEntryFee(yearData?.champion_entry_fee || 5)
      setChampionGameOpen(yearData?.champion_game_open || false)
      
      const { data: { user } } = await supabase.auth.getUser()
      
      if (user) {
        // Query with tournament_teams instead of teams, including tiebreaker data
        const { data } = await supabase
          .from('entries')
          .select(`
            id,
            entry_name,
            points,
            is_paid,
            created_at,
            champion_team_id,
            championship_total_points,
            championship_total_fouls,
            champion_team:tournament_teams!entries_champion_team_id_fkey(id, seed, school:team_schools(name)),
            picks:entry_picks(
              team:tournament_teams(id, seed, region, wins, is_eliminated, school:team_schools(name))
            )
          `)
          .eq('user_id', user.id)
          .eq('year', year)
          .order('created_at', { ascending: false })
        
        // Get champion contest entries for this user
        const { data: championEntries } = await supabase
          .from('champion_entries')
          .select('entry_id, is_paid')
          .eq('user_id', user.id)
          .eq('year', year)
        
        const championEntryMap = new Map(
          championEntries?.map(ce => [ce.entry_id, ce.is_paid]) || []
        )

        // If contest is live, get all entries to calculate rankings
        let rankMap = new Map<string, number>()
        let totalEntries = 0
        
        if (yearData?.contest_live) {
          // Get all entries with their picks to calculate points
          const { data: allEntriesData } = await supabase
            .from('entries')
            .select('id')
            .eq('year', year)
          
          const allEntryIds = allEntriesData?.map((e: SupabaseEntry) => e.id) || []
          totalEntries = allEntryIds.length
          
          // Get all picks to calculate points
          const { data: allPicksData } = await supabase
            .from('entry_picks')
            .select('entry_id, team:tournament_teams(seed, wins)')
            .in('entry_id', allEntryIds)
          
          // Calculate points per entry
          const entryPointsMap = new Map<string, number>()
          allPicksData?.forEach((pick: SupabaseEntry) => {
            const pickTeam = Array.isArray(pick.team) ? pick.team[0] : pick.team
            if (pickTeam) {
              const currentPoints = entryPointsMap.get(pick.entry_id) || 0
              const teamPoints = calculateTeamPoints(pickTeam.seed || 1, pickTeam.wins || 0)
              entryPointsMap.set(pick.entry_id, currentPoints + teamPoints)
            }
          })
          
          // Sort entries by points and assign ranks
          const sortedEntries = Array.from(entryPointsMap.entries())
            .sort((a, b) => b[1] - a[1])
          
          let currentRank = 1
          for (let i = 0; i < sortedEntries.length; i++) {
            if (i > 0 && sortedEntries[i][1] < sortedEntries[i - 1][1]) {
              currentRank = i + 1
            }
            rankMap.set(sortedEntries[i][0], currentRank)
          }
        }

        // Map and calculate points
        const mappedEntries = (data as SupabaseEntry[])?.map((e: SupabaseEntry) => {
          // Handle team being array or object (Supabase nested query quirk)
          // Also flatten school name from join
          const picks = e.picks?.map((p: SupabaseEntry) => {
            const rawTeam = Array.isArray(p.team) ? p.team[0] : p.team
            if (!rawTeam) return null
            const team: Team = {
              id: rawTeam.id,
              name: rawTeam.school?.name || 'Unknown',
              seed: rawTeam.seed,
              region: rawTeam.region,
              wins: rawTeam.wins || 0,
              is_eliminated: rawTeam.is_eliminated || false
            }
            return { team }
          }).filter((p: { team: Team } | null) => p?.team) || []
          // Calculate total points from team wins (seed × round multiplier)
          const calculatedPoints = picks.reduce((sum: number, p: { team: Team }) => 
            sum + calculateTeamPoints(p.team?.seed || 1, p.team?.wins || 0), 0)
          
          // Process champion team data
          const rawChampion = Array.isArray(e.champion_team) ? e.champion_team[0] : e.champion_team
          const champion_team = rawChampion ? {
            id: rawChampion.id,
            name: rawChampion.school?.name || 'Unknown',
            seed: rawChampion.seed
          } : null
          
          return {
            ...e,
            is_paid: e.is_paid === true,  // Ensure boolean (handle null)
            points: calculatedPoints,
            picks,
            rank: rankMap.get(e.id),
            totalEntries,
            champion_team,
            in_champion_contest: championEntryMap.has(e.id),
            champion_contest_paid: championEntryMap.get(e.id) || false
          }
        }) || []

        // Sort by rank (ascending), then alphabetically by entry_name for ties
        mappedEntries.sort((a, b) => {
          // If both have ranks, sort by rank ascending
          if (a.rank !== undefined && b.rank !== undefined) {
            if (a.rank !== b.rank) return a.rank - b.rank
          }
          // If only one has rank, ranked entry comes first
          if (a.rank !== undefined && b.rank === undefined) return -1
          if (a.rank === undefined && b.rank !== undefined) return 1
          // For ties or no ranks, sort alphabetically by entry_name
          return a.entry_name.localeCompare(b.entry_name)
        })

        setEntries(mappedEntries)
      }
      setLoading(false)
    }

    loadEntries()
  }, [])

  const generateVenmoLink = (entryId: string, entryName: string) => {
    const note = encodeURIComponent(`Wright Picks ${activeYear} - ${entryName} (${entryId.slice(0, 8)})`)
    // Use configured payment link, append query params
    const baseUrl = paymentLink.includes('?') ? paymentLink + '&' : paymentLink + '?'
    return `${baseUrl}txn=pay&amount=${entryFee}&note=${note}`
  }

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-500">Loading entries...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">My Entries</h1>
          <p className="text-gray-500">Manage your Wright Picks {activeYear} entries</p>
        </div>
        {entriesOpen && (
          <Link to="/team-picker">
            <Button className="bg-[#1e3a5f] hover:bg-[#002a6b] text-white">
              + New Entry
            </Button>
          </Link>
        )}
      </div>

      {/* Empty State */}
      {entries.length === 0 && (
        <Card className="bg-white border-0 shadow-md">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="12" y1="18" x2="12" y2="12"/>
                <line x1="9" y1="15" x2="15" y2="15"/>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-[#1e3a5f] mb-2">No Entries Yet</h2>
            <p className="text-gray-500 mb-6">
              {entriesOpen 
                ? "Create your first entry and pick 16 teams!" 
                : "Entries are currently closed."}
            </p>
            {entriesOpen && (
              <Link to="/team-picker">
                <Button className="bg-[#1e3a5f] hover:bg-[#002a6b] text-white">
                  Create Entry
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {/* Entries List */}
      <div className="space-y-3">
        {entries.map((entry) => {
          // Calculate max potential from picks
          const teamsForCalc = entry.picks?.map(p => p.team).filter(Boolean) || []
          const maxPotential = teamsForCalc.length > 0 ? calculateMaxPotential(teamsForCalc) : null
          const teamsAlive = entry.picks?.filter(p => !p.team?.is_eliminated).length || 0
          
          return (
          <Card key={entry.id} className="bg-white border border-gray-100 shadow-sm overflow-hidden">
            <CardHeader className="pb-3 pt-4">
              {/* Top row: Rank + Entry name + Payment */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  {/* Rank badge - only show when contest is live */}
                  {contestLive && entry.rank && (
                    <span className={`text-sm font-bold tabular-nums ${
                      entry.rank <= 3 ? 'text-amber-600' : 'text-gray-400'
                    }`}>
                      #{entry.rank}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-[#1e3a5f] text-base font-semibold truncate">{entry.entry_name}</CardTitle>
                  </div>
                </div>
                
                {/* Payment status */}
                {entry.is_paid ? (
                  <span className="text-xs text-green-600 font-medium">Paid</span>
                ) : (
                  <a 
                    href={generateVenmoLink(entry.id, entry.entry_name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-500 hover:text-[#1e3a5f] underline"
                  >
                    Pay ${entryFee}
                  </a>
                )}
              </div>
              
              {/* Stats row - simple inline text */}
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="text-gray-500">
                  <span className="font-semibold text-[#1e3a5f]">{teamsAlive}</span> alive
                </span>
                <span className="text-gray-500">
                  <span className="font-semibold text-green-600 tabular-nums">{entry.points}</span> pts
                </span>
                <span className="text-gray-400">
                  <span className="tabular-nums">{maxPotential ? maxPotential.max : entry.points}</span> max
                </span>
                {contestLive && entry.rank && (
                  <span className="text-gray-400 text-xs">
                    Rank {entry.rank} of {entry.totalEntries}
                  </span>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="pt-0 pb-4">
              {/* Championship Section - Simple and clean */}
              {entry.champion_team && (
                <div className="mb-3 py-2 px-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span className="text-gray-500">
                      Champion: <span className="font-medium text-[#1e3a5f]">({entry.champion_team.seed}) {entry.champion_team.name}</span>
                    </span>
                    <span className="text-gray-400">
                      Pts: <span className="font-medium text-gray-600">{entry.championship_total_points ?? '—'}</span>
                    </span>
                    <span className="text-gray-400">
                      Fouls: <span className="font-medium text-gray-600">{entry.championship_total_fouls ?? '—'}</span>
                    </span>
                    {entry.in_champion_contest && (
                      <span className={`text-xs font-medium ${
                        entry.champion_contest_paid ? 'text-green-600' : 'text-amber-600'
                      }`}>
                        Side Pot {entry.champion_contest_paid ? 'Paid' : `$${championEntryFee}`}
                      </span>
                    )}
                  </div>
                </div>
              )}
            
              {/* Actions row */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                  className="text-[#1e3a5f] hover:text-[#F15A22] text-sm font-medium flex items-center gap-1"
                >
                  {expandedEntry === entry.id ? 'Hide' : 'Show'} Teams
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    className={`transition-transform ${expandedEntry === entry.id ? 'rotate-180' : ''}`}
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                
                {entriesOpen && (
                  <Link 
                    to={`/team-picker?edit=${entry.id}`}
                    className="text-gray-400 hover:text-[#F15A22] text-sm flex items-center gap-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Edit
                  </Link>
                )}
              </div>
              
              {/* Expanded teams view */}
              {expandedEntry === entry.id && entry.picks && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                    {entry.picks
                      .sort((a, b) => a.team.seed - b.team.seed)
                      .map(({ team }) => {
                        const teamPoints = calculateTeamPoints(team?.seed || 1, team?.wins || 0)
                        return (
                          <div 
                            key={team.id}
                            className="flex items-center gap-1 py-1 px-1.5 text-xs"
                          >
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                              team.is_eliminated ? 'bg-red-300' : 'bg-green-400'
                            }`} />
                            <span className={`font-medium w-4 ${team.is_eliminated ? 'text-gray-300' : 'text-gray-400'}`}>
                              {team.seed}
                            </span>
                            <span className={`truncate flex-1 ${team.is_eliminated ? 'text-gray-300 line-through' : 'text-gray-600'}`}>
                              {team.name}
                            </span>
                            <span className={`tabular-nums ${
                              team.is_eliminated ? 'text-gray-300' : teamPoints > 0 ? 'text-green-600' : 'text-gray-300'
                            }`}>
                              {teamPoints}
                            </span>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          )
        })}
      </div>
    </div>
  )
}
